import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getNetwork, NETWORKS, signupUrl } from "@/data/networks";
import { MaskedLines, FadeIn, Reveal } from "@/components/hv/Motion";
import { ArchPhoto } from "@/components/hv/ArchPhoto";
import { Marquee } from "@/components/hv/Marquee";
import { Pillars } from "@/components/hv/Pillars";
import { CtaBand } from "@/components/hv/CtaBand";
import { FollowButton } from "@/components/hv/FollowButton";
import { FeaturedVideo } from "@/components/hv/FeaturedVideo";
import { Testimonials } from "@/components/hv/Testimonials";
import { TourCapture } from "@/components/hv/TourCapture";
import { ChecklistBand } from "@/components/hv/ChecklistBand";
import { Seo } from "@/components/hv/Seo";
import { LogoMark } from "@/components/hv/Logo";

const Posts = ({ n }) => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20" data-testid="posts-section">
    <Reveal>
      <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: n.accent }}>On our {n.name}</div>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1F1A17]">What you'll find <em>when you follow.</em></h2>
      <div className="mt-8"><FollowButton network={n} testId="posts-follow-btn" /></div>
    </Reveal>
    <ol className="border-t border-[#E8DDD3]">
      {n.posts.map((p, i) => (
        <Reveal key={p.t} delay={i * 0.08}>
          <li data-testid={`post-item-${i}`} className="group grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr] gap-4 border-b border-[#E8DDD3] py-8 transition-colors duration-500 hover:bg-white/70">
            <span className="font-mono text-sm text-[#685F58] pt-1.5">0{i + 1}</span>
            <span>
              <span className="block font-serif text-2xl sm:text-3xl tracking-tight text-[#1F1A17] transition-transform duration-500 group-hover:translate-x-2">{p.t}</span>
              <span className="mt-2 block text-sm leading-relaxed text-[#685F58]">{p.d}</span>
            </span>
          </li>
        </Reveal>
      ))}
    </ol>
  </section>
);

const Elsewhere = ({ current }) => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="elsewhere-section">
    <Reveal className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Also find us on</Reveal>
    <div className="mt-6 grid sm:grid-cols-3 gap-4">
      {NETWORKS.filter((x) => x.key !== current).map((x, i) => (
        <Reveal key={x.key} delay={i * 0.08}>
          <Link to={`/${x.key}`} data-testid={`elsewhere-${x.key}`} className="group flex items-center justify-between rounded-2xl border border-[#E8DDD3] bg-white p-5 transition-colors duration-300 hover:border-[#1F1A17]">
            <span className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl grid place-items-center text-white" style={{ background: x.accent }}><x.Icon className="h-4 w-4" /></span>
              <span>
                <span className="block font-serif text-xl text-[#1F1A17]">{x.name}</span>
                <span className="block text-xs text-[#685F58]">{x.audience}</span>
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-[#1F1A17] transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function NetworkPage() {
  const { network } = useParams();
  const n = getNetwork(network);
  if (!n) return <Navigate to="/" replace />;

  return (
    <div data-testid={`network-page-${n.key}`} key={n.key}>
      <Seo title={n.title} description={n.description} image={`/og/${n.key}.jpg`} imageAlt={`HearthVault on ${n.name} — ${n.headline.join(" ")}`} />
      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-20 sm:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-10 items-center">
        <div>
          <FadeIn className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] bg-white px-3 py-1.5">
            <n.Icon className="h-3.5 w-3.5" style={{ color: n.accent }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1F1A17]" data-testid="network-eyebrow">{n.eyebrow}</span>
          </FadeIn>
          <MaskedLines
            testId="network-hero-title"
            className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[84px] leading-[1.02] tracking-tight text-[#1F1A17]"
            lines={n.headline.map((l, i) => (i === n.headline.length - 1 ? <em key={i} className="text-[#B4532A]">{l}</em> : l))}
          />
          <FadeIn delay={0.7}>
            <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#685F58]">{n.intro}</p>
          </FadeIn>
          <FadeIn delay={0.85} className="mt-9 flex flex-wrap gap-3">
            <a href={signupUrl(n.key)} data-testid="network-signup-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#B4532A] hover:bg-[#9C421E] h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
              <LogoMark className="h-4 w-4" /> Create your vault — free
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <FollowButton network={n} testId="network-hero-follow-btn" />
          </FadeIn>
          {n.secondary && (
            <FadeIn delay={1}>
              <a href={n.secondary.href} data-testid="network-secondary-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#B4532A] hover:underline underline-offset-4">
                {n.secondary.label} <ArrowRight className="h-4 w-4" />
              </a>
            </FadeIn>
          )}
          <FadeIn delay={1.05}>
            <p className="mt-6 text-xs text-[#685F58]">No card needed · Encrypted at rest · No trackers, no ads</p>
          </FadeIn>
        </div>
        <ArchPhoto src={n.image} alt={n.imageAlt} accent={n.accent} label={n.audience} />
      </section>

      <Marquee />
      {n.video && <FeaturedVideo n={n} />}
      <Posts n={n} />
      {n.key === "facebook" && <ChecklistBand accent={n.accent} />}
      <Pillars />
      <Testimonials networkKey={n.key} accent={n.accent} />
      <CtaBand source={n.key} follow={<FollowButton network={n} variant="dark" testId="cta-band-follow-btn" />} />
      <TourCapture networkKey={n.key} accent={n.accent} />
      <Elsewhere current={n.key} />
    </div>
  );
}
